
import { CSSProperties } from "react";
import {
  PartialBrandTheme,
  PartialQuestThemeData,
} from "../../tutorial/components/tutorial.types";
import { hexToGlassColor } from "../../../../general";

interface ReferEarnStyleConfig {
  Form?: CSSProperties;
  Heading?: CSSProperties;
  Description?: CSSProperties;
  Label?: CSSProperties;
  Input?: CSSProperties;
  Icon?: CSSProperties;
  PrimaryButton?: CSSProperties;
  Footer?: {
    FooterStyle?: CSSProperties;
    FooterTextStyle?: CSSProperties;
    FooterIcon?: CSSProperties;
    FooterText?: string;
  };
}

interface ReferEarnThemeConfig {
  backgroundColor?: string;
  primaryColor?: string;
  secondaryColor?: string;
  buttonColor?: string;
  borderColor?: string;
  fontFamily?: string;
}

class ReferEarnThemeManager {
  themeConfig: ReferEarnThemeConfig;
  styleConfig: ReferEarnStyleConfig;
  questThemeData: PartialQuestThemeData | undefined;
  brandTheme: PartialBrandTheme | undefined;

  constructor(
    themeConfig: ReferEarnThemeConfig = {},
    styleConfig: ReferEarnStyleConfig = {},
    questThemeData?: PartialQuestThemeData,
    brandTheme?: PartialBrandTheme
  ) {
    this.themeConfig = themeConfig;
    this.styleConfig = styleConfig;
    this.questThemeData = questThemeData;
    this.brandTheme = brandTheme;
  }

  // Main container styles
  getMainContentStyles(): CSSProperties {
    return {
      background: this.styleConfig?.Form?.backgroundColor || this.themeConfig?.backgroundColor,
      height: this.styleConfig?.Form?.height || "auto",
      fontFamily: this.brandTheme?.fontFamily || this.themeConfig.fontFamily || "'Figtree', sans-serif",
      borderRadius: this.styleConfig?.Form?.borderRadius ||
        this.questThemeData?.borderRadius ||
        this.brandTheme?.borderRadius,
      ...this.styleConfig?.Form,
    };
  }

  // Heading styles
  getHeadingStyles(): CSSProperties {
    return {
      color: this.styleConfig?.Heading?.color || this.brandTheme?.titleColor || this.themeConfig?.primaryColor,
      ...this.styleConfig?.Heading,
    };
  }

  // Description styles
  getDescriptionStyles(): CSSProperties {
    return {
      color: this.styleConfig?.Description?.color || this.brandTheme?.contentColor || this.themeConfig?.secondaryColor,
      ...this.styleConfig?.Description,
    };
  }

  // Input/Code box styles
  getReferralCodeStyles() {
    return {
      container: {
        borderColor: this.themeConfig?.borderColor,
        borderRadius: this.styleConfig?.Input?.borderRadius ||
          this.questThemeData?.borderRadius ||
          this.brandTheme?.borderRadius,
        ...this.styleConfig?.Input,
      },
      text: {
        color: this.styleConfig?.Input?.color || 
              this.styleConfig?.Description?.color || 
              this.brandTheme?.contentColor || 
              this.themeConfig?.secondaryColor,
        ...this.styleConfig?.Input,
      },
    };
  }

  // Label styles
  getLabelStyles(): CSSProperties {
    return this.styleConfig?.Label || { color: this.brandTheme?.contentColor } || {};
  }

  // Button styles
  getPrimaryButtonStyles(): CSSProperties {
    return {
      border: this.styleConfig?.PrimaryButton?.border || "none",
      background: this.styleConfig?.PrimaryButton?.background ||
        this.brandTheme?.primaryColor ||
        this.questThemeData?.accentColor ||
        this.themeConfig?.buttonColor,
      borderRadius: this.styleConfig?.PrimaryButton?.borderRadius ||
        this.questThemeData?.borderRadius ||
        this.brandTheme?.borderRadius,
      ...this.styleConfig?.PrimaryButton,
    };
  }

  // Icon styles
  getIconStyles(): CSSProperties {
    return this.styleConfig?.Icon || { background: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.1) : "#E6F0FF"};
  }

  // Footer styles
  getFooterStyles() {
    return {
      container: {
        background: this.styleConfig?.Footer?.FooterStyle?.backgroundColor ||
          this.styleConfig?.Form?.backgroundColor ||
          this.styleConfig?.Form?.background ||
          this.themeConfig?.backgroundColor,
        borderRadius: this.styleConfig?.Form?.borderRadius ||
          `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "10px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "10px"}`,
        ...this.styleConfig?.Footer?.FooterStyle,
      },
      text: this.styleConfig?.Footer?.FooterTextStyle || {},
      icon: this.styleConfig?.Footer?.FooterIcon || {},
      content: {
        text: this.styleConfig?.Footer?.FooterText || "",
      },
    };
  }


  getGradientWrapperStyles(): CSSProperties {
    return {
      background: "white",
      width: "fit-content",
      borderRadius: this.styleConfig?.Form?.borderRadius || 
                   this.questThemeData?.borderRadius || 
                   this.brandTheme?.borderRadius || 
                   "10px",
    };
  }

  getGradientContainerStyles(gradientBackgroundColor?: string): CSSProperties {
    return {
      fontFamily: this.brandTheme?.fontFamily || this.themeConfig.fontFamily || "'Figtree', sans-serif",
      background: gradientBackgroundColor ? gradientBackgroundColor : !(this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? 'linear-gradient(13deg, #fff 38.24%, #9035ff 59.39%, #0065ff 70.7%)' : (this.brandTheme?.primaryColor || this.questThemeData?.accentColor)?.includes("gradient") ? (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) : `linear-gradient(14deg, rgba(255, 255, 255, 0) 38.24%, ${hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.6)} 59.39%, ${this.brandTheme?.primaryColor || this.questThemeData?.accentColor} 70.7%)`,
      borderRadius: this.styleConfig?.Form?.borderRadius ||
        this.questThemeData?.borderRadius ||
        this.brandTheme?.borderRadius ||
        "10px",
    };
  }

  getGradientHeadingStyles(): CSSProperties {
    return {
      ...this.styleConfig?.Heading,
      color: "white"
    };
  }

  getGradientDescriptionStyles(): CSSProperties {
    return {
      ...this.styleConfig?.Description,
      color: "white"
    };
  }


  updateThemeConfig(newConfig: Partial<ReferEarnThemeConfig>): void {
    this.themeConfig = {
      ...this.themeConfig,
      ...newConfig,
    };
  }

  updateStyleConfig(newConfig: Partial<ReferEarnStyleConfig>): void {
    this.styleConfig = {
      ...this.styleConfig,
      ...newConfig,
      // Special handling for nested Footer object
      Footer: {
        ...this.styleConfig?.Footer,
        ...newConfig?.Footer,
      },
    };
  }

  updateQuestTheme(newTheme: PartialQuestThemeData | undefined): void {
    this.questThemeData = newTheme;
  }

  updateBrandTheme(newTheme: PartialBrandTheme | undefined): void {
    this.brandTheme = newTheme;
  }

  // Helper function to merge styles
  mergeStyles(baseStyles: CSSProperties, overrideStyles?: CSSProperties): CSSProperties {
    return {
      ...baseStyles,
      ...(overrideStyles || {}),
    };
  }


  updateAllThemes(updates: {
    themeConfig?: Partial<ReferEarnThemeConfig>;
    styleConfig?: Partial<ReferEarnStyleConfig>;
    questTheme?: PartialQuestThemeData;
    brandTheme?: PartialBrandTheme;
  }): void {
    if (updates.themeConfig) {
      this.updateThemeConfig(updates.themeConfig);
    }
    if (updates.styleConfig) {
      this.updateStyleConfig(updates.styleConfig);
    }
    if (updates.questTheme) {
      this.updateQuestTheme(updates.questTheme);
    }
    if (updates.brandTheme) {
      this.updateBrandTheme(updates.brandTheme);
    }
  }

  getReferIconColors() {
    return {
      primary: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 1) : "#9035FF",
      secondary: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.5) : "#BF8AFF",
    };
  }
}

export default ReferEarnThemeManager;